import { FC, memo, ReactNode, useCallback, useMemo } from 'react';

import {
  NOTIFICATION_DEFAULT_DELAY,
  NOTIFICATION_DEFAULT_ERROR_MESSAGE,
  NotificationColors,
  useNotificationContext,
} from 'application/providers/NotificationProvider';
import {
  useAccountCompanyDeviceRequests,
  useAccountCompanyId,
  useAccountCompanyVideoDemoPeriod,
  useAccountLoading,
} from 'application/hooks';
import { SelectPlayerFlowType, useOnboardingActionContext } from 'application/components/common';
import { PointsListDataItem, SelectedPoint } from 'application/pages/VideoStreamsPage/renderer/types';
import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { useAppDispatch } from 'application/providers/StoreProvider';
import { fetchCurrentUserData } from 'application/store';
import { SelectPlayerContentViewMode } from 'application/components/common/SelectPlayerFlow/types';

import {
  RequestDeviceType,
  useCompanySmartlkStartVideoDemoPeriodMutation,
  useCompanyVideoDeviceRequestMutation,
  VideoStreamDocument,
} from '@shared/api/graphql/generated';
import useBindVideoStreamToPoints from 'domain/media/useBindVideoStreamToPoints';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';

import { TEXTS } from './texts';

interface ActionButtonsProps {
  videoStreamId?: string;
  isDenyCreatePoint: boolean;
  isBindButtonDisabled: boolean;
  withDemoPeriod: boolean;
  showRequestVideoButton: boolean;
  selectedPoints: PointsListDataItem[];
  selectedPoint?: SelectedPoint;
  allPointsIds: string[];
  openModal: <T extends ModalTypes>(
    type: T,
    modalState: Omit<ModalProps[T], 'close' | 'open' | 'openModal' | 'replaceModal'>,
    onModalCloseCallback?: () => void
  ) => void;
  replaceModal: <T extends ModalTypes>(
    type: T,
    modalState: Omit<ModalProps[T], 'open' | 'close' | 'openModal' | 'replaceModal'>,
    onModalCloseCallback?: () => void
  ) => void;
  close: () => void;
}

export const ActionButtons: FC<ActionButtonsProps> = memo(
  ({
    videoStreamId,
    isDenyCreatePoint,
    isBindButtonDisabled,
    withDemoPeriod,
    showRequestVideoButton,
    selectedPoints,
    selectedPoint,
    allPointsIds,
    openModal,
    replaceModal,
    close,
  }) => {
    const dispatch = useAppDispatch();

    const { setCurrentPoint, setFlowType, setContentViewMode } = useOnboardingActionContext();

    const notification = useNotificationContext();

    const currentCompanyId = useAccountCompanyId();
    const videoDemoPeriod = useAccountCompanyVideoDemoPeriod();
    const deviceRequests = useAccountCompanyDeviceRequests();
    const accountLoading = useAccountLoading();

    const [startDemoPeriodMutation, { loading: startDemoPeriodLoading, error }] =
      useCompanySmartlkStartVideoDemoPeriodMutation();
    const [requestVideoMutation, { loading: isVideoRequestLoading }] = useCompanyVideoDeviceRequestMutation();
    const {
      request: bindVideoStreamMutation,
      result: { loading: bindVideoStreamLoading },
    } = useBindVideoStreamToPoints();

    const isDemoPeriodAndVideoRequestLoading = startDemoPeriodLoading || isVideoRequestLoading;

    const bindVideoStream = async (selectedPoints: PointsListDataItem[]) => {
      if (!videoStreamId || !currentCompanyId) {
        return;
      }

      const result = await bindVideoStreamMutation(
        {
          videoStreamId,
          companyId: currentCompanyId,
          pointIds: selectedPoints.map((point) => point.id),
        },
        [VideoStreamDocument]
      );

      if (!result.isOk) {
        notification.showNotification({
          type: NotificationColors.ERROR,
          children:
            result.errors && result.errors.length > 0 ? result.errors[0]?.messages : NOTIFICATION_DEFAULT_ERROR_MESSAGE,
          delay: NOTIFICATION_DEFAULT_DELAY,
        });
      }
    };

    const handleRequestVideo = async () => {
      try {
        const result = await requestVideoMutation({
          variables: { input: { companyId: currentCompanyId } },
        });

        if (!result.data?.companyCreateVideoDeviceRequest?.ok) {
          throw new Error();
        }

        replaceModal(ModalTypes.DEVICE_REQUESTED, {});
      } catch (e) {
        const error = e as Error;

        notification.showErrorNotification({
          children: error.message || NOTIFICATION_DEFAULT_ERROR_MESSAGE,
        });
      }
    };

    const handleAddPointClick = useCallback(async () => {
      await close();
      const modalType = isDenyCreatePoint ? ModalTypes.DENY_OPERATION : ModalTypes.POINT_CREATE;
      openModal(modalType, {});
    }, [openModal, close]);

    const handleSaveClick = async () => {
      try {
        await bindVideoStream(selectedPoints);

        const selectedPointInList = selectedPoint && selectedPoints.find((point) => point.id === selectedPoint.pointId);

        const hasSingleNoDevicePoint =
          (selectedPoints.length === 1 && !selectedPoints[0].device) ||
          (selectedPointInList && !selectedPointInList.device);

        if (hasSingleNoDevicePoint) {
          const isDeviceRequested = deviceRequests?.some((request) => request.pointId === selectedPoints[0].id);
          setCurrentPoint({
            ...selectedPoints[0],
            videoStream: { id: videoStreamId },
            device: undefined,
          });
          setContentViewMode(SelectPlayerContentViewMode.VIDEO_STREAM);
          setFlowType(SelectPlayerFlowType.BIND_VIDEO_STREAM);
          replaceModal(isDeviceRequested ? ModalTypes.DEVICE_BIND_SUCCESS : ModalTypes.SELECT_PLAYER_MODAL, {});
          return;
        }

        replaceModal(ModalTypes.VIDEO_STREAM_BIND_SUCCESS, {
          selectedPoints: selectedPointInList ? [selectedPointInList] : selectedPoints,
        });
      } catch (error: any) {
        notification.showNotification({
          type: NotificationColors.ERROR,
          children: error?.message ? error.message : NOTIFICATION_DEFAULT_ERROR_MESSAGE,
        });
      }
    };

    const handleStartDemoPeriodClick = async () => {
      try {
        const result = await startDemoPeriodMutation({
          variables: { input: { companyId: currentCompanyId } },
        });

        if (!result.data?.smartlkStartVideoDemoPeriod?.ok) {
          throw new Error();
        }

        notification.showInfoNotification({
          children: TEXTS.MODAL_BIND_START_DEMO_PERIOD_SUCCESS_TEXT,
        });

        const point = result.data.smartlkStartVideoDemoPeriod.point;
        const pointsSet = new Set(selectedPoints.map(({ id }) => id));
        const pointsForRequest = allPointsIds
          .filter((id) => pointsSet.has(id))
          .map((id) => ({
            id,
          }));

        await bindVideoStream([...pointsForRequest, { id: point?.id || '' }]);

        setCurrentPoint({
          id: point?.id,
          address: point?.address,
          stream: { id: '' },
          videoStream: { id: '' },
          isEnabled: true,
          isVideoDemo: true,
          isVideoMode: true,
        });

        setFlowType(SelectPlayerFlowType.CREATE_DEMO_POINT);
        setContentViewMode(SelectPlayerContentViewMode.VIDEO_STREAM);
        replaceModal(ModalTypes.SELECT_PLAYER_MODAL, {});
        dispatch(fetchCurrentUserData());
      } catch (e) {
        notification.showErrorNotification({
          children: error?.message || NOTIFICATION_DEFAULT_ERROR_MESSAGE,
        });
      }
    };

    const actionButtons = useMemo(() => {
      const result: ReactNode[] = [
        showRequestVideoButton ? (
          <Button
            key="button_request_video_modal"
            elementId="button_request_video_modal"
            type={ButtonType.PRIMARY}
            onClick={handleRequestVideo}
            isLoading={isDemoPeriodAndVideoRequestLoading}
            isDisabled={deviceRequests.some((request) => request.requestType === RequestDeviceType.Video)}>
            {TEXTS.MODAL_VIDEO_REQUEST_BUTTON_TEXT}
          </Button>
        ) : (
          <Button
            key="button_bind_video_stream_modal"
            elementId="button_bind_video_stream_modal"
            type={ButtonType.PRIMARY}
            isLoading={bindVideoStreamLoading}
            onClick={handleSaveClick}
            isDisabled={isBindButtonDisabled}>
            {TEXTS.MODAL_BIND_BUTTON_SUBMIT_TEXT}
          </Button>
        ),
      ];

      if (!selectedPoint && !videoDemoPeriod) {
        result.push(
          <Button
            key="button_start_demo_period_modal"
            elementId="button_start_demo_period_modal"
            type={ButtonType.SECONDARY}
            isLoading={isDemoPeriodAndVideoRequestLoading || accountLoading}
            onClick={withDemoPeriod ? handleStartDemoPeriodClick : handleAddPointClick}>
            {withDemoPeriod ? TEXTS.MODAL_BIND_START_DEMO_PERIOD_TEXT : TEXTS.MODAL_BIND_ADD_NEW_POINT_TEXT}
          </Button>
        );
      }

      return result;
    }, [
      selectedPoints,
      isBindButtonDisabled,
      isDemoPeriodAndVideoRequestLoading,
      accountLoading,
      bindVideoStreamLoading,
      handleStartDemoPeriodClick,
      bindVideoStream,
      handleSaveClick,
      close,
    ]);

    return actionButtons;
  }
);
